import React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { ReportConfigV2 } from "@/common/report-configuration";
import { DIMENSIONS } from "@/common/dimensions";
import { MEASURES } from "@/common/measures";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { applyFieldNameOverride } from "@/common/user";

interface DataItem {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface ReportTableViewProps {
  data: DataItem[];
  reportConfig: ReportConfigV2;
  showToggle?: boolean;
}

const ReportTableViewV2: React.FC<ReportTableViewProps> = ({
  data,
  reportConfig,
}) => {
  const workspace = useCurrentWorkspace();
  const fieldNameOverrides =
    workspace.dataset_field_configs[reportConfig.data.datasetName]
      ?.fieldNameOverrides;
  if (data.length === 0) {
    return <div>No data available</div>;
  }
  console.log("ReportTableViewV2 data:", data);

  // Special handling for attrition drivers reports
  if (
    reportConfig.data.datasetName === "attrition_drivers" ||
    reportConfig.data.datasetName === "time_to_fill_drivers"
  ) {
    return (
      <div className="h-full">
        <ScrollArea className="h-full w-full">
          <div className="min-w-[400px] relative">
            <div className="grid grid-cols-2 bg-background rounded-t-lg sticky top-0 z-10 shadow-[0_2px_2px_rgba(0,0,0,0.1)]">
              <div className="font-bold p-4">Feature</div>
              <div className="font-bold p-4 text-right">Importance Score</div>
            </div>

            {data.map((row, index) => (
              <div key={index} className="grid grid-cols-2 border-t">
                <div className="p-4">{row.Feature}</div>
                <div className="p-4 text-right">
                  {typeof row.value === "number"
                    ? row.value.toFixed(3)
                    : row.value}
                </div>
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    );
  }

  // Special handling for benchmark reports
  if (reportConfig.data.datasetName === "benchmark") {
    // For benchmark reports, we pivot on DataSource instead of MEASURE
    const BENCHMARK_METADATA_FIELDS = new Set([
      "axis_group",
      "MEASURE",
      "value_num",
      "value_pct",
      "value_num_employees",
      "projected",
      "DataSource", // DataSource becomes a column
      "value",
    ]);

    const allKeys = Object.keys(data[0]);
    const dimensionFields = allKeys.filter(
      (key) => !BENCHMARK_METADATA_FIELDS.has(key),
    );

    // Build display columns: dimension fields + DataSource columns
    const displayColumns: Array<{ key: string; label: string }> = [];

    // Add dimension columns (Gender, Race, etc.)
    dimensionFields.forEach((field) => {
      const dimensionDef = reportConfig.data.dimensions
        .map((dim) => DIMENSIONS[dim])
        .find((def) => def?.field.toLowerCase() === field.toLowerCase());

      displayColumns.push({
        key: field,
        label: dimensionDef?.label || field,
      });
    });

    // Add DataSource columns
    displayColumns.push(
      { key: "Benchmarking", label: "Benchmarking" },
      { key: "Your Representation", label: "Your Representation" },
    );

    // Pivot the data by dimension values
    const pivotedData = new Map<
      string,
      Record<string, string | number | null | undefined>
    >();

    data.forEach((row) => {
      const dimensionKey = dimensionFields
        .map((field) => String(row[field] || ""))
        .join("|");

      if (!pivotedData.has(dimensionKey)) {
        const newRow: Record<string, string | number | null | undefined> = {};
        dimensionFields.forEach((field) => {
          newRow[field] = row[field];
        });
        pivotedData.set(dimensionKey, newRow);
      }

      const pivotedRow = pivotedData.get(dimensionKey)!;
      const dataSource = row.DataSource;
      if (
        dataSource === "Benchmarking" ||
        dataSource === "Your Representation"
      ) {
        pivotedRow[dataSource] = row.value_pct;
      }
    });

    const displayData = Array.from(pivotedData.values());

    const gridTemplateColumns = displayColumns
      .map((_, index) =>
        index === 0 ? "minmax(150px, 1.5fr)" : "minmax(120px, 1fr)",
      )
      .join(" ");

    return (
      <div className="h-full">
        <ScrollArea className="h-full w-full">
          <div className="min-w-[800px] relative">
            {/* Header Row */}
            <div
              className="grid bg-background rounded-t-lg sticky top-0 z-10 shadow-[0_2px_2px_rgba(0,0,0,0.1)]"
              style={{ gridTemplateColumns }}
            >
              {displayColumns.map((col) => (
                <div
                  key={col.key}
                  className="font-bold p-4 text-right break-words first:text-left"
                >
                  {col.label}
                </div>
              ))}
            </div>

            {/* Data Rows */}
            {displayData.map((row, index) => (
              <div
                key={index}
                className="grid border-t"
                style={{ gridTemplateColumns }}
              >
                {displayColumns.map((col) => (
                  <div key={col.key} className="p-4 text-right first:text-left">
                    {row[col.key] !== undefined && row[col.key] !== null
                      ? typeof row[col.key] === "number"
                        ? `${(row[col.key] as number).toFixed(2)}%`
                        : String(row[col.key])
                      : "-"}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" className="mt-2" />
        </ScrollArea>
      </div>
    );
  }

  // Metadata fields to exclude from display
  const METADATA_FIELDS = new Set([
    "axis_group",
    "MEASURE",
    "value_num",
    "value_pct",
    "value_num_employees",
    "projected",
  ]);

  // Get all keys from the data
  const allKeys = Object.keys(data[0]);

  // Separate data fields (measures/dimensions) from metadata
  const dataFields = allKeys.filter((key) => !METADATA_FIELDS.has(key));

  // Build list of columns to display: time dimension first, other dimensions, then measures last
  const displayColumns: Array<{ key: string; label: string }> = [];

  // 1. Add time dimension column first (if present)
  const timeDimensionV2 = reportConfig.data.dimensions
    .map((dim) => DIMENSIONS[dim])
    .find((dimDef) => dimDef?.isTimeDimension);

  if (timeDimensionV2) {
    const timeField = dataFields.find(
      (key) => key.toLowerCase() === timeDimensionV2.field.toLowerCase(),
    );
    if (timeField) {
      displayColumns.push({
        key: timeField,
        label: applyFieldNameOverride(
          timeDimensionV2.label,
          timeDimensionV2.dimension,
          fieldNameOverrides,
        ),
      });
    }
  }

  // 2. Add non-time dimension columns
  reportConfig.data.dimensions.forEach((dimension) => {
    const dimensionDef = DIMENSIONS[dimension];
    if (dimensionDef && !dimensionDef.isTimeDimension) {
      // Find the actual field name in the data (case-insensitive match)
      const actualField = dataFields.find(
        (key) => key.toLowerCase() === dimensionDef.field.toLowerCase(),
      );
      if (actualField) {
        displayColumns.push({
          key: actualField,
          label: applyFieldNameOverride(
            dimensionDef.label,
            dimension,
            fieldNameOverrides,
          ),
        });
      }
    }
  });

  // 3. Add measure columns last
  reportConfig.data.measures.forEach((measure) => {
    const measureDef = MEASURES[measure];
    if (measureDef) {
      // The measure's value is in the "value" field for this measure's rows
      // We'll display this as a separate column
      displayColumns.push({
        key: `measure_${measure}`,
        label: measureDef.label,
      });
    }
  });

  // Pivot the data: group by dimension values, create columns for each measure
  // Key = combination of all dimension values (e.g., "2020-01-01|Male")
  const pivotedData = new Map<
    string,
    Record<string, string | number | null | undefined>
  >();

  data.forEach((row) => {
    // Build a key from all dimension values
    const dimensionValues = displayColumns
      .filter((col) => !col.key.startsWith("measure_"))
      .map((col) => String(row[col.key] || ""))
      .join("|");

    // Get or create the pivoted row
    if (!pivotedData.has(dimensionValues)) {
      const newRow: Record<string, string | number | null | undefined> = {};
      // Copy dimension values
      displayColumns.forEach((col) => {
        if (!col.key.startsWith("measure_")) {
          newRow[col.key] = row[col.key];
        }
      });
      pivotedData.set(dimensionValues, newRow);
    }

    // Add the measure value to the appropriate measure column
    const measureName = row.MEASURE;
    if (measureName) {
      const pivotedRow = pivotedData.get(dimensionValues)!;
      pivotedRow[`measure_${measureName}`] = row.value;
    }
  });

  // Convert map to array
  const displayData = Array.from(pivotedData.values());

  // Calculate grid template columns
  const gridTemplateColumns = displayColumns
    .map((_, index) =>
      index === 0 ? "minmax(150px, 1.5fr)" : "minmax(120px, 1fr)",
    )
    .join(" ");

  return (
    <div className="h-full">
      <ScrollArea className="h-full w-full">
        <div className="min-w-[800px] relative">
          {/* Header Row */}
          <div
            className="grid bg-background rounded-t-lg sticky top-0 z-10 shadow-[0_2px_2px_rgba(0,0,0,0.1)]"
            style={{ gridTemplateColumns }}
          >
            {displayColumns.map((col) => (
              <div
                key={col.key}
                className="font-bold p-4 text-right break-words first:text-left"
              >
                {col.label}
              </div>
            ))}
          </div>

          {/* Data Rows */}
          {displayData.map((row, index) => (
            <div
              key={index}
              className="grid border-t"
              style={{ gridTemplateColumns }}
            >
              {displayColumns.map((col) => (
                <div key={col.key} className="p-4 text-right first:text-left">
                  {row[col.key] !== undefined && row[col.key] !== null
                    ? typeof row[col.key] === "number"
                      ? (row[col.key] as number).toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })
                      : String(row[col.key])
                    : "-"}
                </div>
              ))}
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" className="mt-2" />
      </ScrollArea>
    </div>
  );
};

export default ReportTableViewV2;
